import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Search, FlaskConical, Euro, Handshake, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const INNOVATION_VEHICLES = [
  {
    id: 'V1',
    icon: Search,
    color: '#8DC21F',
    gradient: 'linear-gradient(135deg, rgba(141,194,31,0.2), rgba(141,194,31,0.05))',
    titleES: 'Scouting Tecnológico',
    titleEN: 'Technology Scouting',
    descES: 'Identificación y evaluación de tecnologías y soluciones existentes en el mercado. Partners especializados, startups, spin-offs universitarios y proveedores tecnológicos.',
    descEN: 'Identification and evaluation of existing technologies and solutions in the market. Specialized partners, startups, university spin-offs and technology providers.',
    useCaseES: 'Sensores IoT, soluciones SaaS, plataformas de gestión',
    useCaseEN: 'IoT sensors, SaaS solutions, management platforms',
    timelineES: 'Corto plazo',
    timelineEN: 'Short term',
  },
  {
    id: 'V2',
    icon: FlaskConical,
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, rgba(59,130,246,0.2), rgba(59,130,246,0.05))',
    titleES: 'Pilotos y PoCs',
    titleEN: 'Pilots & PoCs',
    descES: 'Pruebas de concepto y pilotos en entorno real con métricas de impacto. Validación rápida de hipótesis antes de escalado. Aprendizaje iterativo con riesgo controlado.',
    descEN: 'Proof of concepts and pilots in real environment with impact metrics. Rapid hypothesis validation before scaling. Iterative learning with controlled risk.',
    useCaseES: 'Robots humanoides, smart helmets, sistemas de detección de gases',
    useCaseEN: 'Humanoid robots, smart helmets, gas detection systems',
    timelineES: 'Medio plazo',
    timelineEN: 'Medium term',
  },
  {
    id: 'V3',
    icon: Euro,
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, rgba(245,158,11,0.2), rgba(245,158,11,0.05))',
    titleES: 'Ayuda Pública / Fondos EU',
    titleEN: 'Public Funding / EU Funds',
    descES: 'Proyectos I+D+i financiados por CDTI, fondos NextGenerationEU, programas Horizon Europe. Co-desarrollo con centros tecnológicos y universidades.',
    descEN: 'R&D+i projects funded by CDTI, NextGenerationEU funds, Horizon Europe programs. Co-development with technology centers and universities.',
    useCaseES: 'CÍCLICOM, VALDESC, SOS AGUA XXI, proyectos de economía circular',
    useCaseEN: 'CÍCLICOM, VALDESC, SOS AGUA XXI, circular economy projects',
    timelineES: 'Largo plazo',
    timelineEN: 'Long term',
  },
  {
    id: 'V4',
    icon: Handshake,
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, rgba(139,92,246,0.2), rgba(139,92,246,0.05))',
    titleES: 'Innovación Abierta / Ecosistema',
    titleEN: 'Open Innovation / Ecosystem',
    descES: 'Colaboraciones estratégicas con el ecosistema: hubs de innovación (Patio Campus, Area101), centros tecnológicos (CIRCE, Tecnalia), corporate ventures y shakers internos.',
    descEN: 'Strategic collaborations with the ecosystem: innovation hubs (Patio Campus, Area101), technology centers (CIRCE, Tecnalia), corporate ventures and internal shakers.',
    useCaseES: 'TrisTrash, workshops con startups, eventos ReruWaste',
    useCaseEN: 'TrisTrash, startup workshops, ReruWaste events',
    timelineES: 'Variable',
    timelineEN: 'Variable',
  },
];

function VehicleCard({ vehicle, index, inView, lang }) {
  const Icon = vehicle.icon;
  const title = lang === 'es' ? vehicle.titleES : vehicle.titleEN;
  const desc = lang === 'es' ? vehicle.descES : vehicle.descEN;
  const useCase = lang === 'es' ? vehicle.useCaseES : vehicle.useCaseEN;
  const timeline = lang === 'es' ? vehicle.timelineES : vehicle.timelineEN;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
      className="group relative h-full"
    >
      <div
        className="relative h-full rounded-2xl p-6 overflow-hidden transition-all duration-500"
        style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.08)',
          backdropFilter: 'blur(12px)',
        }}
      >
        {/* Gradient overlay on hover */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: vehicle.gradient }}
        />

        {/* Content */}
        <div className="relative z-10">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div 
              className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
              style={{
                background: `${vehicle.color}15`,
                border: `1px solid ${vehicle.color}30`,
              }}
            >
              <Icon size={26} style={{ color: vehicle.color }} />
            </div>

            <div 
              className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full"
              style={{
                background: `${vehicle.color}10`,
                color: vehicle.color,
                border: `1px solid ${vehicle.color}30`,
              }}
            >
              {vehicle.id}
            </div>
          </div>

          {/* Title */}
          <h3 className="text-xl font-semibold text-white mb-4 leading-tight">
            {title}
          </h3>

          {/* Description */}
          <p className="text-sm text-white/60 leading-relaxed mb-5">
            {desc}
          </p>

          {/* Use case */}
          <div 
            className="mb-4 p-3 rounded-xl"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <div className="text-xs font-bold text-white/40 uppercase tracking-wider mb-2">
              {lang === 'es' ? 'Ejemplo de uso' : 'Use case example'}
            </div>
            <p className="text-xs text-white/70 leading-relaxed">
              {useCase}
            </p>
          </div>

          {/* Timeline */}
          <div className="flex items-center gap-2">
            <ArrowUpRight size={14} style={{ color: vehicle.color }} />
            <span className="text-xs font-medium" style={{ color: vehicle.color }}>
              {timeline}
            </span>
          </div>
        </div>

        {/* Hover border */}
        <div 
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{ 
            border: `1px solid ${vehicle.color}40`,
          }}
        />
      </div>
    </motion.div>
  );
}

export default function InnovationVehiclesSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const { lang } = useLanguage();

  return (
    <section
      ref={ref}
      className="relative py-20 sm:py-32 overflow-hidden bg-black dark:bg-black"
      style={{
        background: 'linear-gradient(180deg, #000 0%, rgba(8,3,3,0.98) 50%, #000 100%)',
      }}
    >
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ 
          background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(141,194,31,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="v-container relative z-10">
        {/* Tag */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-4"
        >
          <span className="v-tag green">
            {lang === 'es' ? 'VEHÍCULOS DE INNOVACIÓN' : 'INNOVATION VEHICLES'}
          </span>
        </motion.div>

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-[1.1] tracking-tight">
              {lang === 'es' ? (
                <>
                  4 <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>caminos</em>{' '}
                  para{' '}
                  <span style={{ color: '#8DC21F' }}>ejecutar</span>{' '}
                  innovación
                </>
              ) : (
                <>
                  4 <em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>pathways</em>{' '}
                  to{' '}
                  <span style={{ color: '#8DC21F' }}>execute</span>{' '}
                  innovation
                </>
              )}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col justify-center"
          >
            <p className="text-base text-white/60 leading-relaxed mb-4">
              {lang === 'es'
                ? 'No hay una única forma de innovar. Cada reto requiere un enfoque diferente. Por eso utilizamos 4 vehículos complementarios que nos permiten actuar con velocidad, escala o profundidad según la necesidad.'
                : 'There\'s no single way to innovate. Each challenge requires a different approach. That\'s why we use 4 complementary vehicles that allow us to act with speed, scale or depth according to the need.'}
            </p>
            <p className="text-sm text-white/50 leading-relaxed">
              {lang === 'es'
                ? 'Desde scouting rápido de soluciones hasta proyectos I+D+i de largo plazo financiados con fondos europeos. Flexibilidad para elegir el mejor camino.'
                : 'From rapid solution scouting to long-term R&D+i projects funded with European funds. Flexibility to choose the best path.'}
            </p>
          </motion.div>
        </div>

        {/* Vehicle cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {INNOVATION_VEHICLES.map((vehicle, index) => (
            <VehicleCard
              key={vehicle.id}
              vehicle={vehicle}
              index={index}
              inView={inView}
              lang={lang}
            />
          ))}
        </div>

        {/* Bottom note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <p className="text-sm text-white/50 max-w-2xl mx-auto leading-relaxed">
            {lang === 'es'
              ? 'Cada TOP 3 y TOP 5 será ejecutado mediante uno o varios de estos vehículos. La selección depende del tipo de reto, madurez tecnológica y timeframe requerido.'
              : 'Each TOP 3 and TOP 5 will be executed through one or more of these vehicles. Selection depends on challenge type, technological maturity and required timeframe.'}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
